import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@bindery/ui/components/ui/card";
import { BookOpen, Flame, TrendingUp } from "lucide-react";
import { useMemo } from "react";
import type { LucideIcon } from "lucide-react";
import { timeAgo } from "../time-ago";

const STAR_STRINGS = new Map<number, string>(
  [1, 2, 3, 4, 5].map((n) => [n, "\u2605".repeat(n)]),
);

interface Activity {
  key: string;
  icon: LucideIcon;
  iconColor: string;
  text: string;
  timestamp: number;
}

interface RecentActivityProps {
  stats: {
    recentCompletions: {
      title: string;
      readingId: string;
      completedAt: number;
      stars?: number;
    }[];
    currentStreak: number;
    wordsMastered: number;
    lastReadDate: number | null;
  };
}

export function buildActivities(
  stats: RecentActivityProps["stats"],
): Activity[] {
  const activities: Activity[] = [];

  for (const completion of stats.recentCompletions) {
    const starStr = completion.stars
      ? ` - ${STAR_STRINGS.get(completion.stars) ?? ""}`
      : "";
    activities.push({
      key: `completion-${completion.readingId}`,
      icon: BookOpen,
      iconColor: "text-mintleaf-600",
      text: `Completed "${completion.title}"${starStr}`,
      timestamp: completion.completedAt,
    });
  }

  if (stats.currentStreak >= 3 && stats.lastReadDate) {
    activities.push({
      key: "streak",
      icon: Flame,
      iconColor: "text-flamenco-600",
      text: `${stats.currentStreak}-day reading streak!`,
      timestamp: stats.lastReadDate,
    });
  }

  if (
    stats.wordsMastered > 0 &&
    stats.wordsMastered % 10 === 0 &&
    stats.lastReadDate
  ) {
    activities.push({
      key: "fluency",
      icon: TrendingUp,
      iconColor: "text-skyfall-600",
      text: `${stats.wordsMastered} words mastered`,
      timestamp: stats.lastReadDate,
    });
  }

  return activities.toSorted((a, b) => b.timestamp - a.timestamp).slice(0, 5);
}

export function RecentActivity({ stats }: RecentActivityProps) {
  const displayActivities = useMemo(() => buildActivities(stats), [stats]);

  if (displayActivities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Start reading to see your activity here!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {displayActivities.map((activity) => {
            const Icon = activity.icon;
            return (
              <li key={activity.key} className="flex items-start gap-3">
                <Icon className={`mt-0.5 h-4 w-4 ${activity.iconColor}`} />
                <div className="flex-1">
                  <p className="text-sm">{activity.text}</p>
                  <p className="text-muted-foreground text-xs">
                    {timeAgo(activity.timestamp)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
